<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import type { Schemas } from '#shopware'
import type { ParsedKevelAd } from '../../utils/parseKevelDecision'
import type { ListingItemProduct } from '~/types/ListingItems'
import { loadKevelProducts } from '../../utils/loadKevelProducts'

const props = defineProps<{
    ad: ParsedKevelAd
    aspectRatio: string
    aspectRatioClass: string
    aboveTheFold?: boolean
}>()

const { t } = useI18n()
const { trackClick, isValidTarget } = useKevelAdTracking(props.ad, {
    component: 'content-banner-products',
})

// Product loading state
const productsLoading = ref(true)
const loadedProducts = ref<Schemas['Product'][]>([])

// Convert products to ListingItemProduct format for ProductSlider
const listingProducts = computed<ListingItemProduct[]>(() => {
    return loadedProducts.value.map((product, index) => ({
        pos: index,
        type: 'product' as const,
        data: product
    }))
})

// Background style computation
const backgroundStyle = computed(() => {
    if (props.ad.backgroundImage) {
        return {
            backgroundImage: `url(${props.ad.backgroundImage})`,
            backgroundSize: 'cover',
        }
    }
    if (props.ad.backgroundColor) {
        return {
            backgroundColor: props.ad.backgroundColor
        }
    }
    // Fallback to a default color if neither is provided
    return {
        backgroundColor: '#f3f4f6' // Tailwind gray-100 equivalent
    }
})

// For video: clicking should play/pause (not navigate)
function handleVideoClick(event: MouseEvent) {
    event.stopPropagation() // Prevent navigation
    const video = event.target as HTMLVideoElement
    if (video.paused) {
        video.play()
    } else {
        video.pause()
    }
}

// Handle container click
function handleContainerClick(event: MouseEvent | KeyboardEvent) {
    if (isValidTarget) {
        trackClick(event instanceof MouseEvent ? event : undefined)
    }
}

// Load products on mount
onMounted(async () => {
    if (props.ad.products && props.ad.products.length > 0) {
        loadedProducts.value = await loadKevelProducts(props.ad.products)
    }
    productsLoading.value = false
})
</script>

<template>
    <div
        class="kevel-ad-content-banner-products block relative overflow-hidden bg-[position:left_center] lg:bg-[position:center]"
        :class="{ 'cursor-pointer': isValidTarget }"
        :role="isValidTarget ? 'button' : undefined"
        :tabindex="isValidTarget ? 0 : undefined"
        :style="backgroundStyle"
        v-on="isValidTarget ? {
            click: handleContainerClick,
            'keydown.enter': handleContainerClick,
            'keydown.space': (e: KeyboardEvent) => { e.preventDefault(); handleContainerClick(e); }
        } : {}"
    >
        <div class="relative w-full h-full flex flex-col lg:grid lg:grid-cols-2 lg:gap-10">
            <!-- Asset (16:9 image or video) or transparent space for background image -->
            <div class="aspect-video w-full">
                <!-- If background image exists, show transparent space to let it show through -->
                <div v-if="ad.backgroundImage" class="w-full h-full bg-transparent"/>

                <!-- Otherwise show the asset (video or image) -->
                <template v-else>
                    <MiscVideo
                        v-if="ad.isVideo"
                        :src="ad.assetUrl"
                        :alt="ad.headline ?? t('kevel.ad.defaultAlt')"
                        autoplay
                        muted
                        loop
                        :controls="true"
                        class="w-full h-full object-cover cursor-pointer"
                        @click="handleVideoClick"
                    />
                    <MiscImage
                        v-else
                        :src="ad.assetUrl"
                        :alt="ad.headline ?? t('kevel.ad.defaultAlt')"
                        class="w-full h-full object-cover bg-base-200"
                        :lazy-load="!aboveTheFold"
                        :preload="aboveTheFold ? { fetchPriority: 'high' } : false"
                    />
                </template>
            </div>

            <!-- Right column: Sponsored label + Headline + Products -->
            <div class="px-5 py-6 lg:p-5 xl:px-5 xl:py-6 flex flex-col gap-5 justify-between lg:gap-3 xl:gap-5 lg:aspect-video lg:min-h-0 xl:justify-end">
                <!-- Sponsored label -->
                <p class="text-xs uppercase font-normal text-white tracking-[0.5px]">
                    {{ t('kevel.ad.label') }}
                </p>

                <!-- Headline -->
                <h2 v-if="ad.headline" class="text-4xl md:text-6xl font-normal uppercase font-fjalla-one text-white line-clamp-1">
                    {{ ad.headline }}
                </h2>

                <!-- Products carousel: stop propagation so product clicks work -->
                <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
                <div
                    v-if="!productsLoading && listingProducts.length > 0"
                    @click.stop
                >
                    <ProductSlider
                        :products="listingProducts"
                        :product-qty="{ value: '4', source: 'static' }"
                        :slides-per-view="{
                            xs: 2.75,
                            sm: 4,
                            md: 4,
                            lg: 4,
                            xl: 4
                        }"
                        :display-nav="false"
                        nav-class="text-white"
                        variant="image-only"
                        slider-id="kevel-content-banner-products"
                        slider-title="Sponsored Products"
                        item-list-id="kevel_content_banner_products"
                        item-list-name="Kevel Content Banner Products"
                    />
                </div>

                <!-- Loading placeholder for products: mirrors ProductSlider (2.75 / 4 slides per view,
                     8px gap, 4:5 image-only cards, no nav). Widths use MiscCarousel's formula:
                     (100% - (slidesPerView - 1) * gap) / slidesPerView -->
                <div v-else-if="productsLoading" class="flex gap-2 overflow-hidden">
                    <div
                        v-for="i in 4"
                        :key="i"
                        class="skeleton rounded-none shrink-0 aspect-[4/5] w-[calc((100%-14px)/2.75)] sm:w-[calc((100%-24px)/4)]"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
